// Ann02 (supervision) — per-client pinned announcements.
//
// GET  /api/client-announcements?companyId=<guid>   — active (pinned) first, then newest first
// POST /api/client-announcements                     — create (any staff; body {companyId, kind, text})
// POST /api/client-announcements/{id}/resolve        — resolve (records caller + optional note)
//
// A "cannot-miss" note keyed to a single client, rendered as a prominent amber banner on the
// client / review / bookkeeping surfaces. Anyone (reviewer, bookkeeper, preparer) can create one;
// resolving records who/when. Backed by core.ClientAnnouncement in PtcCore.Data (SQL core).

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from './client';

export type ClientAnnouncementKind = 'review-with-client' | 'info';

export interface ClientAnnouncement {
  id: string;
  companyId: string | null;
  contactId: string | null;
  createdByStaffId: string;
  authorName: string | null;
  kind: ClientAnnouncementKind;
  text: string;
  isActive: boolean;
  resolvedAt: string | null;
  resolvedByStaffId: string | null;
  resolvedByName: string | null;
  resolutionNote: string | null;
  createdAt: string;
}

export interface ClientAnnouncementsResponse {
  items: ClientAnnouncement[];
}

export interface CreateClientAnnouncementBody {
  companyId: string;
  text: string;
  kind?: ClientAnnouncementKind;
}

export const clientAnnouncementKeys = {
  forCompany: (companyId: string) => ['client-announcements', companyId] as const,
};

export const clientAnnouncementsApi = {
  list: (companyId: string): Promise<ClientAnnouncementsResponse> =>
    fetchApi<ClientAnnouncementsResponse>(`/client-announcements?companyId=${encodeURIComponent(companyId)}`),

  create: (body: CreateClientAnnouncementBody): Promise<ClientAnnouncement> =>
    fetchApi<ClientAnnouncement>('/client-announcements', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  resolve: (id: string, resolutionNote?: string): Promise<ClientAnnouncement> =>
    fetchApi<ClientAnnouncement>(`/client-announcements/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolutionNote }),
    }),
};

export function useClientAnnouncements(companyId: string | undefined) {
  return useQuery<ClientAnnouncementsResponse>({
    queryKey: clientAnnouncementKeys.forCompany(companyId ?? ''),
    queryFn: () => clientAnnouncementsApi.list(companyId!),
    enabled: !!companyId,
  });
}

export function useCreateClientAnnouncement(companyId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<CreateClientAnnouncementBody, 'companyId'>) =>
      clientAnnouncementsApi.create({ ...body, companyId: companyId! }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientAnnouncementKeys.forCompany(companyId ?? '') });
    },
  });
}

export function useResolveClientAnnouncement(companyId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolutionNote }: { id: string; resolutionNote?: string }) =>
      clientAnnouncementsApi.resolve(id, resolutionNote),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientAnnouncementKeys.forCompany(companyId ?? '') });
    },
  });
}
